import { useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { ApiError } from '../../lib/api'
import { forgetAllDrafts } from '../../lib/sessionDrafts'
import { login as apiLogin, logout as apiLogout, refreshSession, sessionSupersededTitle } from './api'
import type { AuthSession, AuthUser } from './api'
import { AuthContext, type AuthContextValue, type AuthStatus } from './context'
import { setAccessToken } from './token'

/** The silent refresh fires this long before the access token expires. */
const REFRESH_MARGIN_MS = 60_000

/** Pause before retrying a cookie superseded by another tab. */
const SUPERSEDED_RETRY_MIN_MS = 250
const SUPERSEDED_RETRY_JITTER_MS = 500

/**
 * Terminal ceiling for superseded retries. Sized past the LARGEST rotation grace the
 * API's validator accepts (300 s), not the 30 s default — any valid server
 * configuration must keep the cross-tab contract. In practice the loop ends much
 * earlier: once the server's configured grace expires, a stale cookie answers
 * Invalid, not Superseded.
 */
const SUPERSEDED_GIVE_UP_MS = 320_000

/** Retry delay when a background refresh fails for transient reasons (offline, 5xx). */
const TRANSIENT_RETRY_MS = 30_000

/**
 * Set before the logout request, cleared when the server confirms. If the request
 * dies on the wire, the httpOnly cookie is still alive — the flag makes the next
 * startup finish the logout instead of silently signing the user back in.
 */
const PENDING_LOGOUT_KEY = 'tc.pending-logout'

/** Random spread subtracted from each timer so open tabs never refresh in unison. */
const TIMER_JITTER_MS = 15_000

/**
 * Owns the session: silent refresh on app start (the httpOnly cookie may still hold a
 * live session), a timer that renews the access token before it expires, and the
 * login/logout actions the UI calls.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ status: AuthStatus; user: AuthUser | null }>({
    status: 'initializing',
    user: null,
  })
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // One stable object so effects and the timer never capture stale callbacks.
  const session = useMemo(() => {
    // Bumped by every apply/drop. An in-flight refresh remembers the generation it
    // started in, so a stale result can never clobber a session established after it
    // (e.g. the on-load refresh failing right after the user signed in manually).
    let generation = 0

    // True while a session is applied: decides whether a transient refresh failure
    // has something worth preserving.
    let established = false

    const clearTimer = () => {
      if (refreshTimer.current !== null) {
        clearTimeout(refreshTimer.current)
        refreshTimer.current = null
      }
    }

    const drop = () => {
      generation += 1
      established = false
      clearTimer()
      setAccessToken(null)
      setState({ status: 'anonymous', user: null })
    }

    // The session is over, not merely unreachable: the work pages kept for this tab — a DAT
    // paste under review — go with it. Signing out in one tab ends the session in every other
    // through its next refresh, and each tab keeps its own storage, so each forgets its own
    // (Copilot on #58). A network blip never gets here.
    const end = () => {
      forgetAllDrafts()
      drop()
    }

    const apply = (next: AuthSession) => {
      generation += 1
      established = true
      clearTimer()
      setAccessToken(next.accessToken, next.user.id)
      setState({ status: 'authenticated', user: next.user })

      // The jitter de-synchronizes open tabs: with the same expiresAt they would all
      // fire at the same instant and race the cookie rotation every cycle.
      const delay = Math.max(
        new Date(next.expiresAt).getTime() -
          Date.now() -
          REFRESH_MARGIN_MS -
          Math.random() * TIMER_JITTER_MS,
        5_000,
      )
      refreshTimer.current = setTimeout(refreshInBackground, delay)
    }

    // "Superseded" means another tab won the rotation and the shared jar holds (or is
    // about to hold) its successor: pause, then present that successor. Everything
    // else fails immediately — an anonymous visitor or a dead session should not sit
    // through retries.
    const refreshWithRetries = async (): Promise<AuthSession> => {
      const startedAt = Date.now()
      for (;;) {
        try {
          return await refreshSession()
        } catch (caught) {
          const superseded =
            caught instanceof ApiError && caught.title === sessionSupersededTitle
          // No per-attempt cap: every winner rotates the shared cookie, so the last
          // participant in an N-tab stampede can legitimately lose N-1 rounds. The
          // bound is TIME instead — past the server's grace window this can no
          // longer be a benign race, and the loop must reach a terminal outcome
          // rather than poll forever.
          if (!superseded || Date.now() - startedAt > SUPERSEDED_GIVE_UP_MS) {
            throw caught
          }
          await new Promise((resolve) => {
            setTimeout(
              resolve,
              SUPERSEDED_RETRY_MIN_MS + Math.random() * SUPERSEDED_RETRY_JITTER_MS,
            )
          })
        }
      }
    }

    const refreshInBackground = () => {
      const startedIn = generation
      refreshWithRetries()
        .then((next) => {
          if (generation === startedIn) {
            apply(next)
          }
        })
        .catch((caught) => {
          if (generation !== startedIn) {
            return
          }

          // Only the API saying 401 means the session is over. A timeout, an offline
          // stretch or a 5xx says nothing about the cookie — dropping a live session
          // for those would bounce the user to login for a network blip.
          const terminal = caught instanceof ApiError && caught.status === 401
          if (terminal) {
            end()
            return
          }
          if (!established) {
            drop()
            return
          }

          clearTimer()
          refreshTimer.current = setTimeout(refreshInBackground, TRANSIENT_RETRY_MS)
        })
    }

    return { apply, drop, end, clearTimer, refreshInBackground }
  }, [])

  useEffect(() => {
    // A logout that never reached the API leaves a live cookie behind; finishing it
    // takes priority over renewing the session it was meant to end.
    if (localStorage.getItem(PENDING_LOGOUT_KEY) === '1') {
      apiLogout()
        .then(() => localStorage.removeItem(PENDING_LOGOUT_KEY))
        .catch(() => {
          // Keep the flag: the next startup retries.
        })
        .finally(session.end)
    } else {
      session.refreshInBackground()
    }
    return session.clearTimer
  }, [session])

  const value = useMemo<AuthContextValue>(
    () => ({
      status: state.status,
      user: state.user,
      login: async (email, password) => {
        session.apply(await apiLogin(email, password))
      },
      logout: async () => {
        // Local state drops even if the network call fails — the user asked to leave —
        // but the flag records the unfinished revocation so startup completes it
        // instead of silently signing them back in off the surviving cookie.
        localStorage.setItem(PENDING_LOGOUT_KEY, '1')
        try {
          await apiLogout()
          localStorage.removeItem(PENDING_LOGOUT_KEY)
        } catch {
          // Already handled by design: the pending flag survives and the next
          // startup completes the revocation. Nothing may escape here — callers
          // fire-and-forget this promise.
        } finally {
          // Work a page kept for this tab — a DAT paste under review — is the user's, and
          // goes with them: the next person at this browser starts empty.
          session.end()
        }
      },
    }),
    [state, session],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
